/**
 * fx.ts — currency conversion for multi-currency portfolios.
 *
 * The portfolio math (portfolioCalc) values positions in each holding's
 * NATIVE currency — the currency its trades were logged in (Trade.currency),
 * or, when that's missing, a currency inferred from the ticker's exchange
 * suffix. To report a mixed-currency portfolio in a single BASE currency we
 * need exchange rates. This module is the pure, framework-free core of that:
 * a rate store + lookup, with no I/O. The rates themselves are fetched
 * server-side (nightly warehouse mirror, eodhd-warehouse/mirror_fx_div.py) into KV;
 * this module only consumes the resulting store.
 *
 * RATE STORE SHAPE — USD pivot.
 *   FxStore = { [currency]: { [date]: usdPerUnit } }
 * For each non-USD currency we keep a date→rate series where the value is
 * "USD per 1 unit of that currency" (i.e. Yahoo's `${CCY}USD=X` close, e.g.
 * GBP→1.27 means £1 = $1.27). USD is implicitly 1 and need not appear.
 * Any base/native pair is derived by crossing through USD:
 *   rate(from→to) = usdPer[from] / usdPer[to].
 * Storing one series per currency (rather than per ordered pair) means the
 * cron fetches N series for N currencies, not N² pairs, and any base
 * currency the user picks is handled by the division.
 *
 * SUBUNITS. EODHD quotes some listings in a currency's hundredth — pence
 * (GBX, 4,017 LSE listings in D1 on 2026-10-11), SA cents (ZAC) and agorot
 * (ILA) — and every spelling is read through `canonicalCurrency` first, so
 * the odd 'GBp' means pence and never collides with pounds. A subunit
 * converts through its major currency's series at 1/100, and to or from its
 * own major at exactly 0.01 or 100 whatever the store holds.
 *
 * A MISSING RATE IS NEVER 1. A currency with no series cannot be put in the
 * base currency, and converting it at 1 showed a wrong number with nothing
 * marking it (audit MONEY-10: ₹29,000 read as £29,000). `fxRate` answers
 * null for it, and `fxRateFor` — the shim every book total runs through —
 * answers 0, so its cash, cost and value are LEFT OUT of every base-currency
 * total alike rather than counted at the wrong size; `missingFxCurrencies`
 * names what was left out, for the page to say so. Same-currency pairs and
 * single-currency mode (no fx context) are unaffected: neither needs a rate.
 *
 * DEPENDS ON: calc/dateIndex and calc/tradeTypes only (pure). Consumed by lib/portfolioCalc (Stage 2).
 */
import { lastIndexOnOrBefore } from './dateIndex';
import { isAmountRow } from './tradeTypes';

/** USD-pivot rate store: currency → (date → USD per 1 unit of currency). */
export type FxStore = Record<string, Record<string, number>>;

/**
 * Sorted date-keys per rate series, cached by the series object itself.
 *
 * `fxRate` is called O(chart-days × holdings) times when valuing an equity
 * curve, and the combined "All Portfolios" book multiplies that by every
 * fund's tickers. Re-sorting `Object.keys(series)` on every lookup turned that
 * into an O(n² log n) trap that froze the page. The fxStore object is stable
 * across renders (it comes from React state), so each currency's series is
 * sorted at most once; subsequent lookups binary-search the cached array.
 */
const sortedDatesCache = new WeakMap<Record<string, number>, string[]>();

function sortedDatesFor(series: Record<string, number>): string[] {
  let dates = sortedDatesCache.get(series);
  if (!dates) {
    dates = Object.keys(series).sort();
    sortedDatesCache.set(series, dates);
  }
  return dates;
}

/**
 * Exchange code → native currency, used ONLY as a fallback when a trade or
 * holding carries no explicit `currency`. A ticker is stored in one form, the
 * EODHD source_code (`ISF.LSE`, `7203.TSE`, `SHOP.TO`), and its exchange code
 * maps to the listing currency. Unsuffixed (and unknown-code) tickers default
 * to USD, matching the app's prior implicit assumption.
 */
const EXCHANGE_CURRENCY: Record<string, string> = {
  // EODHD exchange code -> listing currency. Fallback ONLY: an explicit
  // Trade.currency always wins; unknown/US codes default to USD. Every key is
  // an exchange the warehouse holds instruments on (measured in D1
  // 2026-09-25); the Yahoo-only suffixes (L, T, AX, DE, NS, …) went with the
  // one ticker form (WORK.md item 43 step 3), so `ISF.L` is no longer
  // inferred GBP — it is not a code at all.
  LSE: 'GBP',                                              // London
  TSE: 'JPY',                                              // Tokyo (intl-warehouse J-Quants)
  TO: 'CAD', V: 'CAD', NEO: 'CAD',                         // Canada (TSX / TSXV / NEO)
  AU: 'AUD',                                               // Australia (ASX)
  SW: 'CHF',                                               // Switzerland (SIX)
  PA: 'EUR', XETRA: 'EUR', F: 'EUR', STU: 'EUR', MU: 'EUR',
  BE: 'EUR', HA: 'EUR', DU: 'EUR', HM: 'EUR', AS: 'EUR',
  MC: 'EUR', BR: 'EUR', LS: 'EUR', HE: 'EUR', VI: 'EUR', IR: 'EUR', // Euronext + DACH/Nordic-EUR
  NSE: 'INR', BSE: 'INR',                                  // India (NSE / BSE)
  SHG: 'CNY', SHE: 'CNY',                                  // China (Shanghai / Shenzhen)
  KQ: 'KRW', KO: 'KRW',                                    // Korea (KOSDAQ / KOSPI)
  TW: 'TWD', TWO: 'TWD',                                   // Taiwan
  ST: 'SEK',                                               // Stockholm
  OL: 'NOK',                                               // Oslo
  CO: 'DKK',                                               // Copenhagen
  // Quoted in the subunit on most listings (D1 2026-10-11: JSE 715 ZAC of 812,
  // TA 1,549 ILA of 1,761), so the subunit is the better guess; it had been
  // USD, the unknown-code default, for both.
  JSE: 'ZAC',                                              // Johannesburg
  TA: 'ILA',                                               // Tel Aviv
};

/**
 * Index symbols → quote currency. The `.INDX` pseudo-exchange isn't a real
 * listing venue, so without this map every non-US index fell through to the
 * USD default and benchmark curves picked up a spurious USD→base conversion
 * (e.g. `FTSE.INDX` against a GBP base wiggled with cable instead of the
 * index). Keyed by the EODHD code only — the Yahoo caret forms (`^FTSE`, …)
 * were removed 2026-07-17, and the settings-load normaliser that rewrote a
 * saved caret went with the one ticker form (WORK.md item 43 step 3).
 */
const INDEX_CURRENCY: Record<string, string> = {
  // Best-effort; an explicit currency or EXCHANGE_CURRENCY wins. US indices
  // default to USD.
  'FTSE.INDX': 'GBP', 'FTMC.INDX': 'GBP', 'FTAS.INDX': 'GBP',
  'GDAXI.INDX': 'EUR', 'FCHI.INDX': 'EUR', 'STOXX50E.INDX': 'EUR', 'IBEX.INDX': 'EUR', 'AEX.INDX': 'EUR',
  'N225.INDX': 'JPY', 'HSI.INDX': 'HKD', 'AXJO.INDX': 'AUD', 'SSMI.INDX': 'CHF',
  'KS11.INDX': 'KRW', 'TWII.INDX': 'TWD', 'BSESN.INDX': 'INR', 'NSEI.INDX': 'INR',
  'OMX.INDX': 'SEK', 'OSEAX.INDX': 'NOK',
};

/**
 * Best-effort native currency for a ticker from its exchange suffix (or,
 * for `.INDX` index codes, the INDEX_CURRENCY map).
 * Fallback only — an explicit Trade.currency always wins upstream.
 */
export function nativeCurrencyForTicker(ticker: string): string {
  const up = ticker.toUpperCase();
  const idx = INDEX_CURRENCY[up];
  if (idx) return idx;
  const dot = up.lastIndexOf('.');
  if (dot >= 0) {
    const suffix = up.slice(dot + 1);
    if (EXCHANGE_CURRENCY[suffix]) return EXCHANGE_CURRENCY[suffix];
  }
  return 'USD';
}

/**
 * THE native currency of a trade / holding row: the explicit `currency` when
 * present and non-empty, else inferred from the ticker's exchange suffix.
 * Every engine (holdings, equity curve, cash rules, tax replay) resolves
 * through here so they cannot disagree — before this seam, taxCalc used
 * `??` where the others used `||`, so an empty-string currency silently
 * meant "already in base" on the Tax page but "infer from suffix" everywhere
 * else.
 */
export function resolveTradeCurrency(t: { currency?: string; ticker: string }): string {
  return canonicalCurrency(t.currency) || nativeCurrencyForTicker(t.ticker);
}

/**
 * Currency subunits: the hundredth a listing may be quoted in, with its ISO
 * major currency and how many make one. The same table as the data Worker's
 * (`data-worker/src/symbols/_refdb.ts`), which normalises dividends with it.
 */
export const CURRENCY_SUBUNITS: Readonly<Record<string, { major: string; scale: number }>> = {
  GBX: { major: 'GBP', scale: 100 },
  ZAC: { major: 'ZAR', scale: 100 },
  ILA: { major: 'ILS', scale: 100 },
};

/**
 * THE one reading of a currency code: trimmed and upper-cased, except the
 * mixed-case subunit spellings, read BEFORE upper-casing because
 * upper('GBp') is pounds. '' for a missing or blank code.
 */
export function canonicalCurrency(code: string | null | undefined): string {
  const s = (code ?? '').trim();
  if (s === 'GBp') return 'GBX';
  if (s === 'ZAc') return 'ZAC';
  return s.toUpperCase();
}

/** A code's major currency and the units of it one unit of the code is worth. */
function majorOf(ccy: string): { major: string; per: number } {
  const sub = CURRENCY_SUBUNITS[ccy];
  return sub ? { major: sub.major, per: 1 / sub.scale } : { major: ccy, per: 1 };
}

/**
 * USD per 1 unit of `ccy` at (or, forward-filled, before) `date`, `ccy`
 * already canonical and a MAJOR currency. Null when there is no usable entry.
 * USD is the pivot and always returns 1.
 *
 * Forward-fill: pick the latest dated entry on or before `date`. For a date
 * earlier than the first known rate, back-fill to the earliest entry (better
 * than refusing to convert an early point on the equity curve). `date = null`
 * means "latest available".
 */
function usdPerUnit(store: FxStore, ccy: string, date: string | null): number | null {
  if (ccy === 'USD') return 1;
  const series = store[ccy];
  if (!series) return null;
  const dates = sortedDatesFor(series);
  if (dates.length === 0) return null;
  // A malformed store entry (missing / null / 0 / NaN) reads as "no rate",
  // never as a NaN that would spread through every converted value.
  const finitePos = (v: number | undefined): number | null => (Number.isFinite(v) && (v as number) > 0 ? (v as number) : null);
  if (date == null) return finitePos(series[dates[dates.length - 1]]);
  // Latest dated entry on or before `date` (forward fill) — O(log n), which
  // matters because this runs once per holding per chart day.
  const idx = lastIndexOnOrBefore(dates, date);
  const chosen = idx === -1 ? dates[0] : dates[idx]; // target precedes all data → earliest
  return finitePos(series[chosen]);
}

/**
 * Multiplicative rate to convert an amount in `from` into `to` at `date`,
 * or null when either side has no rate (see the file header: never 1).
 * Codes are read through `canonicalCurrency`; the same currency is exactly 1,
 * and a subunit and its own major (GBX → GBP) are exactly 0.01 or 100,
 * needing no series.
 */
export function fxRate(
  store: FxStore,
  from: string,
  to: string,
  date: string | null = null,
): number | null {
  const f = majorOf(canonicalCurrency(from));
  const t = majorOf(canonicalCurrency(to));
  if (f.major === t.major) return f.per / t.per;
  const usdFrom = usdPerUnit(store, f.major, date);
  const usdTo = usdPerUnit(store, t.major, date);
  if (usdFrom == null || usdTo == null) return null;
  return (usdFrom * f.per) / (usdTo * t.per);
}

/**
 * Rate that converts `ccy` into the context's base currency at `date` — the
 * shim every book total (cash, cost, value, tax) runs through. With no fx
 * context (single-currency mode) it is exactly 1, and an empty/undefined
 * `ccy` is already in base. A currency with no rate answers 0: the amount is
 * LEFT OUT of the base-currency total rather than counted at 1, and
 * `missingFxCurrencies` names it (file header).
 */
export function fxRateFor(
  fx: { store: FxStore; base: string } | undefined,
  ccy: string | undefined,
  date: string | null = null,
): number {
  if (!fx) return 1;
  return fxRate(fx.store, ccy || fx.base, fx.base, date) ?? 0;
}

/**
 * The currencies among `currencies` that cannot be put in `fx.base` — no
 * series for them, or none for the base itself — canonical, sorted, each
 * once. [] with no fx context, where nothing is converted.
 */
export function missingFxCurrencies(
  fx: { store: FxStore; base: string } | undefined,
  currencies: Iterable<string | null | undefined>,
): string[] {
  if (!fx) return [];
  const out = new Set<string>();
  for (const c of currencies) {
    const ccy = canonicalCurrency(c);
    if (ccy && fxRate(fx.store, ccy, fx.base, null) == null) out.add(ccy);
  }
  return [...out].sort();
}

/**
 * Every currency a trade log is denominated in — each trade's own (cash
 * flows included, a blank one being the base's) — for `missingFxCurrencies`.
 */
export function tradeCurrencies(
  trades: Iterable<{ currency?: string; ticker: string; type?: string }>,
  base: string,
): Set<string> {
  const out = new Set<string>();
  for (const t of trades) {
    const cashFlow = t.type != null && isAmountRow(t.type);
    out.add(cashFlow ? (canonicalCurrency(t.currency) || canonicalCurrency(base)) : resolveTradeCurrency(t));
  }
  return out;
}
